import React, { useEffect, useState } from "react";
import { Flex, Text } from "@jsq/ds/core";

type Props = {
  title: string;
  subTitle?: React.ReactNode;
  icon: React.ReactNode;
  children?: React.ReactNode;
  delay?: number; // milliseconds
};

const StateTemplate = ({ title, subTitle, icon, children, delay = 0 }: Props) => {
  const [showSubTitle, setShowSubTitle] = useState(false);

  // Depend on stable inputs — a boolean and the numeric delay — rather than the
  // subTitle node itself. When subTitle is JSX it gets a new reference on every
  // render, which would otherwise reset this timer on innocuous rerenders and
  // could delay or prevent the subTitle from ever showing when delay > 0.
  const hasSubTitle = Boolean(subTitle);

  useEffect(() => {
    if (!hasSubTitle) {
      return;
    }

    const timer = setTimeout(() => {
      // We plan on showing the subTitle, but we need to determine when with {delay}
      setShowSubTitle(true);
    }, delay); // Delay showing the subTitle if we have a delay set.

    // Clear the timer if the component unmounts before {delay} seconds
    return () => clearTimeout(timer);
  }, [hasSubTitle, delay]);

  return (
    <Flex justifyContent="center">
      <Flex
        css={{ margin: "0 auto", paddingTop: "120px", maxWidth: "600px" }}
        alignItems="center"
        flexDirection="column"
      >
        {icon}
        <Text css={{ marginTop: "25px" }} variant="h3">
          {title}
        </Text>
        {subTitle && showSubTitle && (
          <Text css={{ marginTop: "25px", textAlign: "center" }} variant="secondary-md">
            {subTitle}
          </Text>
        )}
        {children && (
          <Flex css={{ marginTop: "25px" }} alignItems="center" justifyContent="space-between">
            {children}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default StateTemplate;
